import React from "react";
import { getPrimaryVariantChoices } from "../presentation/availability";

const PrimaryVariantChips = ({
  product,
  language = "english",
  selection = {},
  availabilityContext = {},
  onChange,
  onOverflow,
  color = "#0099cc",
}) => {
  const { dimension, choices } = getPrimaryVariantChoices(product, {
    ...availabilityContext,
    language,
    selection,
  });
  if (!dimension || !choices.length) return null;
  const visibleChoices = choices.slice(0, 4);
  const overflowCount = Math.max(choices.length - visibleChoices.length, 0);
  const getUnavailableLabel = (choice) => {
    const reason = choice.unavailableReason;
    if (reason === "not_available_in_branch") {
      return language === "arabic"
        ? "غير متوفر في الفرع المحدد"
        : "Unavailable at the selected branch";
    }
    if (reason === "not_available") {
      return language === "arabic" ? "غير متاح" : "Unavailable";
    }
    if (reason && reason !== "out_of_stock") {
      return String(reason).replace(/_/g, " ");
    }
    return language === "arabic" ? "غير متوفر حالياً" : "Out of stock";
  };

  return (
    <div
      aria-label={language === "arabic" ? dimension.labelAr : dimension.label}
      data-primary-variant-scroll="true"
      role="group"
      style={{
        direction: language === "arabic" ? "rtl" : "ltr",
        display: "flex",
        gap: 6,
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "auto",
        overflowY: "hidden",
        overscrollBehaviorX: "contain",
        padding: "2px 0 6px",
        scrollbarWidth: "none",
        touchAction: "pan-x",
        WebkitOverflowScrolling: "touch",
        width: "100%",
      }}
    >
      {visibleChoices.map((choice) => {
        const unavailableLabel = getUnavailableLabel(choice);
        return (
          <button
            aria-disabled={!choice.available}
            aria-label={
              choice.available
                ? choice.label
                : `${choice.label} — ${unavailableLabel}`
            }
            aria-pressed={choice.selected}
            data-available={choice.available}
            key={choice.value}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (choice.available && onChange) onChange(dimension, choice);
            }}
            style={{
              background: choice.selected ? color : "white",
              border: `1px solid ${choice.selected ? color : "#c8c8c8"}`,
              borderStyle: choice.available ? "solid" : "dashed",
              borderRadius: 14,
              color: choice.selected
                ? "white"
                : choice.available
                ? "#333"
                : "#555",
              cursor: choice.available ? "pointer" : "help",
              flex: "0 0 auto",
              fontFamily: "inherit",
              fontSize: 12,
              maxWidth: 96,
              overflow: "hidden",
              padding: "4px 10px",
              textOverflow: "ellipsis",
              textDecoration: choice.available ? "none" : "line-through",
              whiteSpace: "nowrap",
            }}
            title={
              choice.available
                ? choice.label
                : `${choice.label} — ${unavailableLabel}`
            }
            type="button"
          >
            <span>{choice.label}</span>
            {!choice.available && (
              <small
                style={{
                  display: "block",
                  fontSize: 9,
                  lineHeight: 1.1,
                  textDecoration: "none",
                }}
              >
                {unavailableLabel}
              </small>
            )}
          </button>
        );
      })}
      {overflowCount > 0 && (
        <button
          aria-label={
            language === "arabic"
              ? `${overflowCount} خيارات إضافية`
              : `${overflowCount} more options`
          }
          style={{
            alignItems: "center",
            background: "transparent",
            border: 0,
            color: "#555",
            cursor: onOverflow ? "pointer" : "default",
            display: "inline-flex",
            flex: "0 0 auto",
            fontSize: 12,
            fontWeight: 600,
            padding: "4px 2px",
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (onOverflow) onOverflow();
          }}
          title={
            language === "arabic"
              ? `${overflowCount} خيارات إضافية`
              : `${overflowCount} more options`
          }
          type="button"
        >
          +{overflowCount}
        </button>
      )}
    </div>
  );
};

export default PrimaryVariantChips;
